import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { Mold, Task } from '../types/mold';

interface MoldState {
  molds: Mold[];
  tasks: Task[];
  loading: boolean;
  error: string | null;
}

type MoldAction =
  | { type: 'FETCH_MOLDS_START' }
  | { type: 'FETCH_MOLDS_SUCCESS'; payload: Mold[] }
  | { type: 'FETCH_MOLDS_ERROR'; payload: string }
  | { type: 'UPDATE_MOLD'; payload: Mold }
  | {
      type: 'UPDATE_TASK_STATUS';
      payload: { id: string; status: Task['status'] };
    };

interface MoldContextType {
  state: MoldState;
  dispatch: React.Dispatch<MoldAction>;
}

const initialState: MoldState = {
  molds: [],
  tasks: [],
  loading: false,
  error: null,
};

const MoldContext = createContext<MoldContextType | undefined>(undefined);

const moldReducer = (state: MoldState, action: MoldAction): MoldState => {
  switch (action.type) {
    case 'FETCH_MOLDS_START':
      return { ...state, loading: true };
    case 'FETCH_MOLDS_SUCCESS':
      return { ...state, loading: false, molds: action.payload };
    case 'FETCH_MOLDS_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_MOLD':
      return {
        ...state,
        molds: state.molds.map((mold) =>
          mold.id === action.payload.id ? action.payload : mold,
        ),
      };
    case 'UPDATE_TASK_STATUS':
      return {
        ...state,
        tasks: state.tasks.map((task) =>
          task.id === action.payload.id
            ? { ...task, status: action.payload.status }
            : task,
        ),
      };
    default:
      return state;
  }
};

export const MoldProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(moldReducer, initialState);

  return (
    <MoldContext.Provider value={{ state, dispatch }}>
      {children}
    </MoldContext.Provider>
  );
};

export const useMoldContext = () => {
  const context = useContext(MoldContext);
  if (context === undefined) {
    throw new Error('useMoldContext must be used within a MoldProvider');
  }
  return context;
};
